import { useCallback, useEffect, useRef, useState } from "react";
import {
  deleteApiData,
  fetchApiData,
  patchApiData,
  postApiData,
} from "../serviceFile";

export interface SavedReport {
  SavedReportParamId: string;
  SavedReportParamName: string;
  ReportCode: string;
  // Nullable on purpose: a row can carry no usable saved values (never
  // populated, or written before the column became jsonb). loadReportParams
  // treats anything that is not a plain object as "no values" and renders every
  // field empty.
  ReportParams: Record<string, any> | null;
  IsFavourite: boolean;
  CreatedAt?: string;
  UpdatedAt?: string;
}

// Backend enforces the same cap (409 SAVED_REPORTS_LIMIT_REACHED); this constant
// only drives the "N/10" badge and the disabled Save button, so the limit is a
// real guarantee rather than a client-side-only suggestion.
export const SAVED_REPORTS_LIMIT = 10;

const READ_URL = "<<BASE_URL>>/report/saved-reports";
const WRITE_URL = "<<BASE_URL>>/user/saved-reports";

/**
 * Owns the Saved Reports ("My Reports") list for one report, plus its CRUD
 * calls — a genuinely separate concern from useReportForm's local field editing,
 * hence its own hook rather than an extension of that one.
 *
 * No userId is read or threaded through: identity comes from the headers
 * prepareRequestConfig already attaches to every request, and the backend
 * derives CreatedBy/ownership from them server-side.
 */
export const useSavedReports = (reportCode: string | undefined) => {
  const [list, setList] = useState<SavedReport[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // The report this hook is CURRENTLY showing. ReportForm doesn't remount on
  // report switch (selectedReport changes as a prop, not a key), so this one
  // hook instance lives across switches — a refresh/save/remove/update call
  // started for the previous reportCode can still resolve after the switch.
  // Each async operation below closes over the `reportCode` it was STARTED
  // with; comparing that to this ref's CURRENT value after awaiting is how it
  // detects "the user has since moved to a different report" and skips
  // applying its result, rather than overwriting the new report's list/total
  // with the old report's response.
  const currentReportCode = useRef(reportCode);
  useEffect(() => {
    currentReportCode.current = reportCode;
  }, [reportCode]);

  const refresh = useCallback(async () => {
    if (!reportCode) {
      setList([]);
      setTotal(0);
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      const response = await fetchApiData(READ_URL, { ReportCode: reportCode });
      if (currentReportCode.current !== reportCode) return; // stale — report changed mid-request
      const rows: SavedReport[] = response?.list ?? [];
      setList(rows);
      setTotal(response?.pagination?.total ?? rows.length);
    } catch (e) {
      if (currentReportCode.current !== reportCode) return;
      console.error("Failed to load saved reports", e);
      setError("Failed to load My Reports");
      setList([]);
      setTotal(0);
    } finally {
      if (currentReportCode.current === reportCode) setIsLoading(false);
    }
  }, [reportCode]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  /**
   * `reportParams` is the REPORT-GENERATION PAYLOAD — the same
   * `{ reportParam: value }` object Generate Report posts — stored verbatim,
   * NOT this hook's own raw per-field form state. That is what lets a saved
   * row run as-is, and what useReportForm.loadReportParams inverts to feed it
   * straight back into the form fields.
   *
   * Throws on failure so the caller can surface the backend's own message
   * (duplicate name, limit reached) rather than silently swallowing it.
   */
  const save = useCallback(
    async (name: string, reportParams: Record<string, any>, isFavourite = false) => {
      if (!reportCode) return null;

      const response = await postApiData(WRITE_URL, {
        ReportCode: reportCode,
        ReportParams: reportParams,
        SavedReportParamName: name.trim(),
        IsFavourite: isFavourite,
      });

      // The POST response carries the created row; prepend it so the newest
      // saved report shows first, matching the list's CreatedAt DESC order.
      const created: SavedReport | undefined =
        response?.SavedReportParamId ? response : response?.data;

      // The save itself succeeded server-side regardless of what the user is
      // looking at now, so still return `created` below — but don't touch
      // list/total for a report the user has since navigated away from.
      if (currentReportCode.current === reportCode) {
        if (created?.SavedReportParamId) {
          setList((prev) => [created, ...prev]);
          setTotal((prev) => prev + 1);
        } else {
          await refresh();
        }
      }
      return created ?? null;
    },
    [reportCode, refresh]
  );

  const remove = useCallback(
    async (savedReportParamId: string) => {
      await deleteApiData(WRITE_URL, {
        SavedReportParamId: savedReportParamId,
      });
      if (currentReportCode.current !== reportCode) return; // stale — report changed mid-request
      setList((prev) =>
        prev.filter((row) => row.SavedReportParamId !== savedReportParamId)
      );
      setTotal((prev) => Math.max(0, prev - 1));
    },
    [reportCode]
  );

  /**
   * Rename and/or toggle favourite — the client for the PATCH endpoint.
   *
   * KNOWN FOLLOW-UP, not an oversight: nothing in the panel calls this yet —
   * v1 deliberately ships no rename or favourites UI (the list has no
   * favourites-first ordering and the GET has no IsFavourite filter/sort to
   * feed one). It lives here so the endpoint has its one obvious caller when
   * that UI is built, rather than the shape being reinvented elsewhere. Build
   * the UI for this (or remove it) in the follow-up ticket rather than letting
   * it sit indefinitely.
   */
  const update = useCallback(
    async (
      savedReportParamId: string,
      changes: { SavedReportParamName?: string; IsFavourite?: boolean }
    ) => {
      await patchApiData(WRITE_URL, {
        SavedReportParamId: savedReportParamId,
        ...changes,
      });
      if (currentReportCode.current !== reportCode) return; // stale — report changed mid-request
      setList((prev) =>
        prev.map((row) =>
          row.SavedReportParamId === savedReportParamId
            ? { ...row, ...changes }
            : row
        )
      );
    },
    [reportCode]
  );

  /**
   * Client-side duplicate check, matching the backend's case-insensitive,
   * trimmed comparison.
   *
   * A UX shortcut on top of — never instead of — the backend's own
   * SAVED_REPORT_NAME_ALREADY_EXISTS, which stays authoritative: this in-memory
   * list can be stale if another tab or device saved the same name since this
   * page loaded.
   */
  const isDuplicateName = useCallback(
    (name: string) => {
      const normalised = (name ?? "").trim().toLowerCase();
      if (!normalised) return false;
      return list.some(
        (row) =>
          (row.SavedReportParamName ?? "").trim().toLowerCase() === normalised
      );
    },
    [list]
  );

  return {
    list,
    total,
    isLoading,
    error,
    isAtLimit: total >= SAVED_REPORTS_LIMIT,
    refresh,
    save,
    remove,
    update,
    isDuplicateName,
  };
};
